import { useAnytimeProductList } from '../Queries/useAnytimeProductList';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { useInitiateAnytimeTransaction } from '../Mutations/useInitiateAnytimeTransaction';
import { useAnytimeTicketStore } from '../State/AnytimeTicketContext';
import { store } from 'Models/Store';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { useProductList } from 'Modules/Products/Hooks/useProductList';
import {
	formatCustomerName,
	formatCustomerOption,
} from 'Views/Components/_HumanWritten/CustomerSelection/CustomerOptionFormatter';
import { useCustomerSearch } from 'Views/Components/_HumanWritten/CustomerSelection/Queries/useCustomerSearch';
import { AsyncSelectInput } from 'Views/Components/New/AsyncSelectInput';
import { AddNewCustomerButton } from 'Views/Components/_HumanWritten/CustomerSelection/AddNewCustomerButton';
import AnytimeTicketProductItem from './AnytimeTicketProductItem';
import InputCounter from 'Views/Components/_HumanWritten/PassengerAmount/InputCounter';
import { getMessageByKey } from 'Stores/LanguageStore';

const CustomerSearchInput = ({ className, hasTickets }: { className?: string; hasTickets: boolean }) => {
	const selectedCustomer = useAnytimeTicketStore(s => s.selectedCustomer);
	const selectCustomer = useAnytimeTicketStore(s => s.selectCustomer);
	const selectedProductType = useAnytimeTicketStore(s => s.selectedProductType);
	const customerError = useAnytimeTicketStore(s => s.customerError);
	const { loadOptions } = useCustomerSearch();

	const isPosSelected = selectedProductType === 'POS';
	// A customer is only required when there are tickets to buy; otherwise it's optional.
	const isCustomerOptional = isPosSelected || !hasTickets;

	return (
		<AsyncSelectInput
			className={className}
			error={customerError || undefined}
			label={
				<>
					Select customer
					{isCustomerOptional ? (
						<span className="ml-1 font-normal text-gray-400">(optional)</span>
					) : (
						<span className="ml-1 text-red-500">*</span>
					)}
				</>
			}
			placeholder="By name, email or phone"
			loadOptions={loadOptions}
			defaultOptions
			cacheOptions
			isClearable
			value={selectedCustomer}
			onChange={(option: any) => {
				selectCustomer(option || null);
			}}
			getOptionLabel={(option: any) => formatCustomerName(option)}
			getOptionValue={(option: any) => option.id}
			formatOptionLabel={formatCustomerOption as any}
		/>
	);
};

export default function AnytimeTicketProductStep() {
	const selectedProductId = useAnytimeTicketStore(s => s.selectedProductId);
	const selectProduct = useAnytimeTicketStore(s => s.selectProduct);
	const productQuantity = useAnytimeTicketStore(s => s.productQuantity);
	const setProductQuantity = useAnytimeTicketStore(s => s.setProductQuantity);
	const selectedCustomerId = useAnytimeTicketStore(s => s.selectedCustomerId);
	const startRegisterCustomer = useAnytimeTicketStore(s => s.startRegisterCustomer);

	const { data, isLoading, isError } = useAnytimeProductList(selectedCustomerId);

	if (isLoading) {
		return (
			<div className="py-8 text-center">
				<LottieSpinner />
			</div>
		);
	}

	if (isError) {
		return (
			<div className="py-8 text-center text-red-600">
				<p>Error loading products. Please try again later.</p>
			</div>
		);
	}

	const products = data?.filter(p => !p.isDisabled && (p.type !== 'POS' || store.can('SellPosProducts'))) || [];

	if (products.length === 0) {
		return (
			<div className="py-8 text-center">
				<p>No products available at this time.</p>
			</div>
		);
	}

	const posProducts = products.filter(p => p.type === 'POS');
	const ticketProducts = products.filter(p => p.type === 'TICKET');

	// Staff must select a customer before the anytime ticket options are shown.
	// POS items are always available and do not require a customer.
	const ticketsGated = store.isStaff && !selectedCustomerId;

	const renderProduct = (product: (typeof products)[number]) => (
		<AnytimeTicketProductItem
			key={`${product.type}-${product.id}`}
			product={product}
			isSelected={selectedProductId === product.id}
			quantity={selectedProductId === product.id ? productQuantity : 1}
			onQuantityChange={setProductQuantity}
			onSelect={id => selectProduct(id, product.type)}
		/>
	);

	return (
		<>
			<div className="flex flex-col gap-4">
				<h3 className="mb-6 text-xl font-semibold text-left">
					{store.isStaff
						? 'Select items'
						: getMessageByKey('anytime-ticket-purchase__select-product__title', 'Select Passenger Tickets')}
				</h3>
				<div className="flex flex-col gap-4">
					{store.isStaff && (
						<div className="customer-select-row mb-4" data-test-id="anytime-ticket-customer-search">
							<CustomerSearchInput hasTickets={ticketProducts.length > 0} />
							<AddNewCustomerButton onClick={startRegisterCustomer} />
						</div>
					)}
					{ticketsGated
						? ticketProducts.length > 0 && (
								<div
									className="py-6 text-center text-gray-400"
									data-test-id="anytime-ticket-select-customer-prompt"
								>
									<p>Select a customer to view ticket options.</p>
								</div>
							)
						: ticketProducts.map(renderProduct)}
					{posProducts.map(renderProduct)}
				</div>
			</div>
		</>
	);
}

interface FooterProps {
	onCancel: () => void;
	onDone: () => void;
	scrollToTop: () => void;
}

AnytimeTicketProductStep.Footer = function AnytimeTicketProductFooter({ onCancel }: FooterProps) {
	const startCheckout = useAnytimeTicketStore(s => s.startCheckout);
	const goToTerms = useAnytimeTicketStore(s => s.goToTerms);
	const selectedProductId = useAnytimeTicketStore(s => s.selectedProductId);
	const selectedProductType = useAnytimeTicketStore(s => s.selectedProductType);
	const selectedCustomerId = useAnytimeTicketStore(s => s.selectedCustomerId);
	const productQuantity = useAnytimeTicketStore(s => s.productQuantity);
	const setProductQuantity = useAnytimeTicketStore(s => s.setProductQuantity);
	const setCustomerError = useAnytimeTicketStore(s => s.setCustomerError);

	const { data } = useProductList();
	const product = data?.items.find(p => p.id === selectedProductId);
	const hasTerms = !!(product?.termsAndConditions || whiteLabelStore.config?.defaultProductTerms);

	const { mutate: initiateTransaction, isPending } = useInitiateAnytimeTransaction({
		onSuccess: response => {
			startCheckout(response.transactionId, response.userEntitlementId, response.purchasedItemId);
		},
	});

	const isPosProdutSelected = selectedProductType === 'POS' && selectedProductId;

	const isStaffMissingCustomerForTicket = store.isStaff && selectedProductType === 'TICKET' && !selectedCustomerId;
	// Leave Continue clickable when only the customer is missing so the click can
	// surface a validation error on the Select customer field.
	const isProceedDisabled = isPending || !selectedProductId;

	return (
		<>
			{isPosProdutSelected && (
				<NewModal.Footer>
					{/* Quantity Selector for POS products */}
					<InputCounter
						className="!p-0  !border-none"
						title="Quantity"
						amount={productQuantity}
						subtractDisabled={productQuantity <= 1}
						onSubtract={() => setProductQuantity(Math.max(1, productQuantity - 1))}
						onAdd={() => setProductQuantity(productQuantity + 1)}
					/>
				</NewModal.Footer>
			)}
			<NewModal.Footer>
				<Button display={Display.Outline} colors={Colors.Grey} newStyles onClick={onCancel}>
					Cancel
				</Button>
				<Button
					display={Display.Solid}
					colors={Colors.Primary}
					newStyles
					onClick={() => {
						if (!selectedProductId) return;
						if (isStaffMissingCustomerForTicket) {
							setCustomerError('Please select a customer to continue.');
							return;
						}
						if (hasTerms) {
							goToTerms();
						} else {
							initiateTransaction({
								productId: selectedProductId,
								productType: selectedProductType || 'TICKET',
								customerId: selectedCustomerId,
								quantity: productQuantity,
							});
						}
					}}
					disabled={isProceedDisabled}
					buttonProps={{ 'data-test-id': 'anytime-ticket-product-continue' } as any}
				>
					{isPending ? 'Processing...' : 'Continue'}
				</Button>
			</NewModal.Footer>
		</>
	);
};
